import { useState } from 'react'
import { FormProvider, useFieldArray, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Plus, Save, Trash2 } from 'lucide-react'

import type { CreateInNoteItemInput } from '@/lib/api/agri-material-lot-api'
import type { AgriInNoteFormValues } from '@/lib/schemas/agri-note-schemas'
import { createAgriInNoteSchema } from '@/lib/schemas/agri-note-schemas'
import {
    AgriLotPicker,
    AgriMaterialPicker,
} from '@/components/agricultural-materials/agri-pickers'
import { PageBackButton } from '@/components/common/PageBackButton'
import { agriUnitLabel } from '@/components/agricultural-materials/agri-unit-label'
import { StickyFormFooter } from '@/components/common/StickyFormFooter'
import { Button } from '@/components/ui/button'
import { FieldError } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { QuantityInput } from '@/components/ui/quantity-input'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { m } from '@/lib/paraglide/messages'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'

export interface AgriInSubmit {
    reason: AgriInNoteFormValues['reason']
    notes?: string
    items: CreateInNoteItemInput[]
}

/**
 * Form phiếu nhập kho (PNK) vật tư nông nghiệp — dùng chung cho màn tạo và sửa
 * (chỉ khác `mode`). Mỗi dòng: LÔ MỚI (tạo lô: mã/NCC/NSX/HSD, qty-mode ẩn các
 * trường lô) HOẶC LÔ CÓ SẴN (chọn lô để nhập thêm). Cùng khung với PNK kho bao
 * bì (custom items field, không dùng LotItemsField vì có NCC/NSX/HSD).
 */
export function AgriInNoteForm({
    mode = 'create',
    initialValues,
    isPending,
    onSubmit,
    onCancel,
}: {
    mode?: 'create' | 'edit'
    initialValues?: AgriInNoteFormValues
    isPending: boolean
    onSubmit: (
        value: AgriInSubmit,
        workflow: ReturnType<typeof useWorkflowTracking>,
    ) => void
    onCancel: () => void
}) {
    const isEdit = mode === 'edit'
    const workflow = useWorkflowTracking('agricultural_stock_in', {
        operation: isEdit ? 'update' : 'create',
    })
    // material_id → tracking_mode, tích luỹ từ chính lượt chọn (picker phân trang
    // nên không có sẵn cả danh sách để tra). Chỉ đọc cho dòng kind='new'.
    const [materialMode, setMaterialMode] = useState<
        Map<string, 'lot' | 'quantity'>
    >(new Map())
    const rememberMode = (id: string, trackingMode: 'lot' | 'quantity') =>
        setMaterialMode((prev) => new Map(prev).set(id, trackingMode))
    // Đơn vị (nhãn VN) để hiện hậu tố ở ô số lượng. Tích luỹ từ lượt chọn:
    // material_id → đơn vị cho LÔ MỚI; lot_id → đơn vị cho LÔ CÓ SẴN.
    const [materialUnit, setMaterialUnit] = useState<Map<string, string>>(
        new Map(),
    )
    const rememberMaterialUnit = (id: string, unit: string) =>
        setMaterialUnit((prev) => new Map(prev).set(id, unit))
    // Seed sẵn đơn vị lô từ initialValues (màn sửa) để ô số lượng có hậu tố ngay,
    // không phải chờ user chọn lại lô. BE trả material_unit (enum) trong item phiếu.
    const [lotUnit, setLotUnit] = useState<Map<string, string>>(() => {
        const seed = new Map<string, string>()
        for (const it of initialValues?.items ?? []) {
            if (
                it.kind === 'existing' &&
                it.agri_material_lot_id &&
                it.material_unit
            )
                seed.set(
                    it.agri_material_lot_id,
                    agriUnitLabel(it.material_unit),
                )
        }
        return seed
    })
    const rememberLotUnit = (id: string, unit: string) =>
        setLotUnit((prev) => new Map(prev).set(id, unit))

    const form = useForm<AgriInNoteFormValues>({
        resolver: zodResolver(createAgriInNoteSchema()),
        defaultValues: initialValues ?? {
            reason: 'from_supplier',
            notes: '',
            items: [{ kind: 'new', new_lot_material_id: '', quantity: 0 }],
        },
    })
    const { register, watch, setValue } = form
    const { fields, append, remove, update } = useFieldArray({
        control: form.control,
        name: 'items',
    })

    const submit = form.handleSubmit(
        (values) => {
            const items: CreateInNoteItemInput[] = values.items.map((it) => {
                if (it.kind === 'existing') {
                    return {
                        agri_material_lot_id: it.agri_material_lot_id,
                        quantity: Number(it.quantity),
                    }
                }
                const isQty =
                    materialMode.get(it.new_lot_material_id) === 'quantity'
                if (isQty) {
                    return {
                        new_lot_material_id: it.new_lot_material_id,
                        quantity: Number(it.quantity),
                    }
                }
                return {
                    new_lot_material_id: it.new_lot_material_id,
                    quantity: Number(it.quantity),
                    ...(it.new_lot_code
                        ? { new_lot_code: it.new_lot_code }
                        : {}),
                    ...(it.new_lot_supplier_name
                        ? { new_lot_supplier_name: it.new_lot_supplier_name }
                        : {}),
                    ...(it.new_lot_manufactured_date
                        ? {
                              new_lot_manufactured_date:
                                  it.new_lot_manufactured_date,
                          }
                        : {}),
                    ...(it.new_lot_expiry_date
                        ? { new_lot_expiry_date: it.new_lot_expiry_date }
                        : {}),
                }
            })
            onSubmit(
                {
                    reason: values.reason,
                    notes: values.notes || undefined,
                    items,
                },
                workflow,
            )
        },
        () => workflow.step('validation_failed'),
    )

    return (
        <div className="space-y-4 page-pad pb-24 md:pb-6">
            <div className="flex items-center gap-3">
                <PageBackButton
                    onBack={() => {
                        workflow.finish('closed')
                        onCancel()
                    }}
                />
                <h1 className="text-xl font-semibold">
                    {isEdit
                        ? m.aml_form_edit_pnk_title()
                        : m.aml_form_create_pnk_title()}
                </h1>
            </div>

            <FormProvider {...form}>
                <form
                    onSubmit={submit}
                    onFocusCapture={() => workflow.interact('details')}
                    noValidate
                    className="max-w-5xl space-y-6"
                >
                    <div className="grid gap-4 rounded-md border bg-card p-4 md:grid-cols-2">
                        <div>
                            <Label>{m.aml_form_reason()}</Label>
                            <Select
                                value={watch('reason')}
                                disabled={isEdit}
                                onValueChange={(v) =>
                                    setValue(
                                        'reason',
                                        v as AgriInNoteFormValues['reason'],
                                    )
                                }
                            >
                                <SelectTrigger className="w-full">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="from_supplier">
                                        {m.aml_in_reason_from_supplier()}
                                    </SelectItem>
                                    <SelectItem value="adjustment">
                                        {m.aml_in_reason_adjustment()}
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>{m.aml_form_notes()}</Label>
                            <Input {...register('notes')} />
                        </div>
                    </div>

                    <div className="space-y-3">
                        <Label>{m.lot_items_title()}</Label>

                        {fields.length === 0 && (
                            <div className="rounded-md border border-dashed bg-muted/30 py-6 text-center text-sm text-muted-foreground">
                                {m.lot_items_empty()}
                            </div>
                        )}

                        {fields.map((field, idx) => {
                            const kind = watch(`items.${idx}.kind`)
                            const materialID = watch(
                                `items.${idx}.new_lot_material_id`,
                            )
                            // Task 1: luôn ẩn trường lô (mã/NCC/NSX/HSD) ở chế độ
                            // "Lô mới" — nhập kho chỉ nhập số lượng, không phụ
                            // thuộc đã chọn vật tư hay chưa.
                            const isQty = kind === 'new'
                            return (
                                <div
                                    key={field.id}
                                    className="grid gap-3 rounded-md border bg-card p-3"
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs font-medium text-muted-foreground">
                                            {m.lot_items_row({ n: idx + 1 })}
                                        </span>
                                        <div className="flex items-center gap-2">
                                            <div className="flex rounded-md border p-0.5 text-xs">
                                                <button
                                                    type="button"
                                                    className={`rounded px-2 py-1 ${kind === 'new' ? 'bg-primary text-primary-foreground' : ''}`}
                                                    onClick={() =>
                                                        update(idx, {
                                                            kind: 'new',
                                                            new_lot_material_id:
                                                                '',
                                                            quantity: 0,
                                                        })
                                                    }
                                                >
                                                    {m.lot_items_new_lot()}
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`rounded px-2 py-1 ${kind === 'existing' ? 'bg-primary text-primary-foreground' : ''}`}
                                                    onClick={() =>
                                                        update(idx, {
                                                            kind: 'existing',
                                                            agri_material_lot_id:
                                                                '',
                                                            quantity: 0,
                                                        })
                                                    }
                                                >
                                                    {m.lot_items_existing_lot()}
                                                </button>
                                            </div>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => remove(idx)}
                                            >
                                                <Trash2 className="size-4 text-destructive" />
                                            </Button>
                                        </div>
                                    </div>

                                    {kind === 'new' && (
                                        <div className="grid gap-3 md:grid-cols-3">
                                            <div>
                                                <Label className="text-xs">
                                                    {m.aml_form_material()}
                                                </Label>
                                                <AgriMaterialPicker
                                                    value={materialID}
                                                    onChange={(mat) => {
                                                        setValue(
                                                            `items.${idx}.new_lot_material_id`,
                                                            mat?.id ?? '',
                                                        )
                                                        if (mat) {
                                                            rememberMode(
                                                                mat.id,
                                                                mat.tracking_mode,
                                                            )
                                                            rememberMaterialUnit(
                                                                mat.id,
                                                                agriUnitLabel(
                                                                    mat.unit,
                                                                ),
                                                            )
                                                        }
                                                        if (
                                                            mat?.tracking_mode ===
                                                            'quantity'
                                                        )
                                                            setValue(
                                                                `items.${idx}.new_lot_code`,
                                                                '',
                                                            )
                                                    }}
                                                />
                                                <FieldError
                                                    name={`items.${idx}.new_lot_material_id`}
                                                />
                                            </div>
                                            {!isQty && (
                                                <div>
                                                    <Label className="text-xs">
                                                        {m.aml_form_lot_code()}
                                                    </Label>
                                                    <Input
                                                        {...register(
                                                            `items.${idx}.new_lot_code`,
                                                        )}
                                                        placeholder="PB-LOT-..."
                                                    />
                                                </div>
                                            )}
                                            <div>
                                                <Label className="text-xs">
                                                    {m.aml_form_quantity()}
                                                </Label>
                                                <QuantityInput
                                                    unit={
                                                        materialUnit.get(
                                                            watch(
                                                                `items.${idx}.new_lot_material_id`,
                                                            ),
                                                        ) || undefined
                                                    }
                                                    type="number"
                                                    step="0.001"
                                                    min={0}
                                                    {...register(
                                                        `items.${idx}.quantity`,
                                                        { valueAsNumber: true },
                                                    )}
                                                />
                                                <FieldError
                                                    name={`items.${idx}.quantity`}
                                                />
                                            </div>
                                            {!isQty && (
                                                <>
                                                    <div>
                                                        <Label className="text-xs">
                                                            {m.aml_form_supplier()}
                                                        </Label>
                                                        <Input
                                                            {...register(
                                                                `items.${idx}.new_lot_supplier_name`,
                                                            )}
                                                            placeholder="VD: Cty ABC"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label className="text-xs">
                                                            {m.aml_form_mfg_date()}
                                                        </Label>
                                                        <Input
                                                            type="date"
                                                            {...register(
                                                                `items.${idx}.new_lot_manufactured_date`,
                                                            )}
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label className="text-xs">
                                                            {m.aml_form_exp_date()}
                                                        </Label>
                                                        <Input
                                                            type="date"
                                                            {...register(
                                                                `items.${idx}.new_lot_expiry_date`,
                                                            )}
                                                        />
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    )}

                                    {kind === 'existing' && (
                                        <div className="grid gap-3 md:grid-cols-2">
                                            <div>
                                                <Label className="text-xs">
                                                    {m.lot_items_select_lot()}
                                                </Label>
                                                <AgriLotPicker
                                                    value={watch(
                                                        `items.${idx}.agri_material_lot_id`,
                                                    )}
                                                    fallbackLabel={
                                                        watch(
                                                            `items.${idx}.material_name`,
                                                        ) ||
                                                        watch(
                                                            `items.${idx}.lot_code`,
                                                        )
                                                    }
                                                    onChange={(lotId, lot) => {
                                                        setValue(
                                                            `items.${idx}.agri_material_lot_id`,
                                                            lotId,
                                                        )
                                                        if (lot?.material_unit)
                                                            rememberLotUnit(
                                                                lotId,
                                                                agriUnitLabel(
                                                                    lot.material_unit,
                                                                ),
                                                            )
                                                    }}
                                                />
                                                <FieldError
                                                    name={`items.${idx}.agri_material_lot_id`}
                                                />
                                            </div>
                                            <div>
                                                <Label className="text-xs">
                                                    {m.aml_form_quantity()}
                                                </Label>
                                                <QuantityInput
                                                    unit={
                                                        lotUnit.get(
                                                            watch(
                                                                `items.${idx}.agri_material_lot_id`,
                                                            ),
                                                        ) || undefined
                                                    }
                                                    type="number"
                                                    step="0.001"
                                                    min={0}
                                                    {...register(
                                                        `items.${idx}.quantity`,
                                                        { valueAsNumber: true },
                                                    )}
                                                />
                                                <FieldError
                                                    name={`items.${idx}.quantity`}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )
                        })}

                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() =>
                                append({
                                    kind: 'new',
                                    new_lot_material_id: '',
                                    quantity: 0,
                                })
                            }
                        >
                            <Plus className="mr-1 size-4" /> {m.lot_items_add()}
                        </Button>
                    </div>

                    {form.formState.errors.items?.message && (
                        <p className="text-sm text-destructive">
                            {form.formState.errors.items.message}
                        </p>
                    )}

                    <StickyFormFooter>
                        <Button
                            type="button"
                            variant="outline"
                            className="h-11 flex-1 md:h-10 md:flex-none"
                            onClick={() => {
                                workflow.finish('closed')
                                onCancel()
                            }}
                        >
                            {m.aml_form_cancel()}
                        </Button>
                        <Button
                            type="submit"
                            className="h-11 flex-1 md:h-10 md:flex-none"
                            disabled={isPending}
                        >
                            <Save className="mr-1 size-4" />
                            {m.aml_form_save()}
                        </Button>
                    </StickyFormFooter>
                </form>
            </FormProvider>
        </div>
    )
}
